import React, { useState } from 'react';

function VaccinationSchedule({ profile, onClose }) {
  const calculateAgeInMonths = (birthDate) => {
    const birth = new Date(birthDate);
    const now = new Date();
    const months = (now.getFullYear() - birth.getFullYear()) * 12 + now.getMonth() - birth.getMonth();
    return Math.max(0, months);
  };

  const ageInMonths = profile ? calculateAgeInMonths(profile.birthDate) : 0;

  // 대한민국 표준 예방접종 스케줄 (질병관리청 기준)
  const vaccinationSchedule = [
    {
      category: '결핵',
      vaccine: 'BCG (피내용)',
      doses: [
        { timing: '생후 4주 이내', months: 0, description: '출생 후 4주 이내 1회 접종' }
      ],
      color: '#E57373'
    },
    {
      category: 'B형간염',
      vaccine: 'HepB',
      doses: [
        { timing: '출생 직후', months: 0, description: '출생 후 24시간 이내' },
        { timing: '생후 1개월', months: 1, description: '1차 접종 후 1개월' },
        { timing: '생후 6개월', months: 6, description: '2차 접종 후 5개월' }
      ],
      color: '#F06292'
    },
    {
      category: '디프테리아/파상풍/백일해',
      vaccine: 'DTaP',
      doses: [
        { timing: '생후 2개월', months: 2, description: '1차 기본접종' },
        { timing: '생후 4개월', months: 4, description: '2차 기본접종' },
        { timing: '생후 6개월', months: 6, description: '3차 기본접종' },
        { timing: '생후 15-18개월', months: 15, description: '4차 추가접종' },
        { timing: '만 4-6세', months: 48, description: '5차 추가접종' }
      ],
      color: '#BA68C8'
    },
    {
      category: '폴리오',
      vaccine: 'IPV',
      doses: [
        { timing: '생후 2개월', months: 2, description: '1차 기본접종' },
        { timing: '생후 4개월', months: 4, description: '2차 기본접종' },
        { timing: '생후 6개월', months: 6, description: '3차 기본접종' },
        { timing: '만 4-6세', months: 48, description: '4차 추가접종' }
      ],
      color: '#9575CD'
    },
    {
      category: '헤모필루스 인플루엔자',
      vaccine: 'Hib',
      doses: [
        { timing: '생후 2개월', months: 2, description: '1차 기본접종' },
        { timing: '생후 4개월', months: 4, description: '2차 기본접종' },
        { timing: '생후 6개월', months: 6, description: '3차 기본접종' },
        { timing: '생후 12-15개월', months: 12, description: '4차 추가접종' }
      ],
      color: '#7986CB'
    },
    {
      category: '폐렴구균',
      vaccine: 'PCV',
      doses: [
        { timing: '생후 2개월', months: 2, description: '1차 기본접종' },
        { timing: '생후 4개월', months: 4, description: '2차 기본접종' },
        { timing: '생후 6개월', months: 6, description: '3차 기본접종' },
        { timing: '생후 12-15개월', months: 12, description: '4차 추가접종' }
      ],
      color: '#64B5F6'
    },
    {
      category: '로타바이러스',
      vaccine: 'RV (선택)',
      doses: [
        { timing: '생후 2개월', months: 2, description: '1차 접종' },
        { timing: '생후 4개월', months: 4, description: '2차 접종' },
        { timing: '생후 6개월', months: 6, description: '3차 접종 (백신 종류에 따라)' }
      ],
      color: '#4FC3F7',
      optional: true
    },
    {
      category: '홍역/유행성이하선염/풍진',
      vaccine: 'MMR',
      doses: [
        { timing: '생후 12-15개월', months: 12, description: '1차 접종' },
        { timing: '만 4-6세', months: 48, description: '2차 접종' }
      ],
      color: '#4DB6AC'
    },
    {
      category: '수두',
      vaccine: 'VAR',
      doses: [
        { timing: '생후 12-15개월', months: 12, description: '1회 접종' }
      ],
      color: '#81C784'
    },
    {
      category: 'A형간염',
      vaccine: 'HepA',
      doses: [
        { timing: '생후 12-23개월', months: 12, description: '1차 접종' },
        { timing: '1차 후 6-12개월', months: 18, description: '2차 접종' }
      ],
      color: '#AED581'
    },
    {
      category: '일본뇌염 (불활성화)',
      vaccine: 'JE (사백신)',
      doses: [
        { timing: '생후 12-23개월', months: 12, description: '1차 접종' },
        { timing: '1차 후 1-2주', months: 12, description: '2차 접종' },
        { timing: '2차 후 12개월', months: 24, description: '3차 접종' }
      ],
      color: '#FFD54F'
    },
    {
      category: '인플루엔자',
      vaccine: 'IIV (독감)',
      doses: [
        { timing: '생후 6개월-만 13세', months: 6, description: '매년 접종 (처음 2회, 이후 매년 1회)' }
      ],
      color: '#FFB74D',
      annual: true
    }
  ];

  const getVaccineStatus = (months) => {
    if (ageInMonths >= months) {
      return 'due'; // 접종 시기가 됨
    } else if (ageInMonths >= months - 1) {
      return 'upcoming'; // 접종 시기가 곧 다가옴
    }
    return 'future'; // 아직 접종 시기가 아님
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'due': return '#FFB74D';
      case 'upcoming': return '#81C784';
      default: return '#E0E0E0';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'due': return '📍 접종 시기';
      case 'upcoming': return '📅 곧 접종';
      default: return '예정';
    }
  };

  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // 현재 접종해야 할 백신과 곧 접종할 백신 개수
  const getDueCounts = () => {
    let dueCount = 0;
    let upcomingCount = 0;

    vaccinationSchedule.forEach(vaccine => {
      vaccine.doses.forEach(dose => {
        const status = getVaccineStatus(dose.months);
        if (status === 'due') dueCount++;
        if (status === 'upcoming') upcomingCount++;
      });
    });

    return { dueCount, upcomingCount };
  };

  const { dueCount, upcomingCount } = getDueCounts();

  return (
    <div className="fade-in" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div className="slide-up" style={{
        backgroundColor: 'white',
        borderRadius: '24px',
        padding: '40px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#8B7355'
          }}
        >
          ×
        </button>

        <div style={{
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>💉</div>
          <h2 style={{
            color: '#E8A87C',
            fontSize: '28px',
            marginBottom: '8px'
          }}>
            예방접종 스케줄
          </h2>
          <p style={{
            color: '#8B7355',
            fontSize: '14px',
            marginBottom: '16px'
          }}>
            {profile.name}이의 현재 월령: {ageInMonths}개월
          </p>

          {/* 접종 현황 요약 */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{
              backgroundColor: '#FFE5B4',
              borderRadius: '12px',
              padding: '12px',
              border: '2px solid #FFB74D'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#F57C00',
                marginBottom: '4px'
              }}>
                {dueCount}
              </div>
              <div style={{ fontSize: '12px', color: '#8B7355' }}>
                접종 시기
              </div>
            </div>
            <div style={{
              backgroundColor: '#E8F5E9',
              borderRadius: '12px',
              padding: '12px',
              border: '2px solid #81C784'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#388E3C',
                marginBottom: '4px'
              }}>
                {upcomingCount}
              </div>
              <div style={{ fontSize: '12px', color: '#8B7355' }}>
                곧 접종
              </div>
            </div>
          </div>

          <div style={{
            backgroundColor: '#FAF3E8',
            borderRadius: '12px',
            padding: '12px',
            fontSize: '12px',
            color: '#8B7355'
          }}>
            💡 질병관리청 표준 예방접종 일정 기준
          </div>
        </div>

        {/* 백신 리스트 */}
        <div style={{ marginBottom: '20px' }}>
          {vaccinationSchedule.map((vaccine, index) => {
            const isExpanded = expandedCategories[vaccine.category];
            const anyDue = vaccine.doses.some(dose => {
              const status = getVaccineStatus(dose.months);
              return status === 'due';
            });

            return (
              <div
                key={index}
                style={{
                  backgroundColor: '#FAF3E8',
                  borderRadius: '16px',
                  marginBottom: '12px',
                  overflow: 'hidden',
                  border: anyDue ? '2px solid #FFB74D' : '2px solid transparent'
                }}
              >
                {/* 백신 헤더 */}
                <div
                  onClick={() => toggleCategory(vaccine.category)}
                  style={{
                    padding: '16px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: vaccine.optional ? '#F5F5F5' : 'transparent'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px'
                    }}>
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: vaccine.color
                        }}
                      />
                      <h3 style={{
                        color: '#E8A87C',
                        fontSize: '16px',
                        margin: 0,
                        fontWeight: 'bold'
                      }}>
                        {vaccine.category}
                      </h3>
                      {vaccine.optional && (
                        <span style={{
                          fontSize: '10px',
                          backgroundColor: '#E0E0E0',
                          color: '#757575',
                          padding: '2px 8px',
                          borderRadius: '8px'
                        }}>
                          선택
                        </span>
                      )}
                      {vaccine.annual && (
                        <span style={{
                          fontSize: '10px',
                          backgroundColor: '#FFE5B4',
                          color: '#F57C00',
                          padding: '2px 8px',
                          borderRadius: '8px'
                        }}>
                          매년
                        </span>
                      )}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#8B7355'
                    }}>
                      {vaccine.vaccine} • {vaccine.doses.length}회 접종
                    </div>
                  </div>
                  <div style={{
                    fontSize: '20px',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}>
                    ▼
                  </div>
                </div>

                {/* 접종 일정 상세 */}
                {isExpanded && (
                  <div style={{ padding: '0 16px 16px 16px' }}>
                    {vaccine.doses.map((dose, doseIndex) => {
                      const status = getVaccineStatus(dose.months);
                      return (
                        <div
                          key={doseIndex}
                          style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '12px',
                            marginBottom: doseIndex < vaccine.doses.length - 1 ? '8px' : '0',
                            borderLeft: `4px solid ${getStatusColor(status)}`
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '8px'
                          }}>
                            <div style={{
                              fontWeight: 'bold',
                              color: '#E8A87C',
                              fontSize: '14px'
                            }}>
                              {dose.timing}
                            </div>
                            <div style={{
                              fontSize: '11px',
                              color: getStatusColor(status),
                              fontWeight: 'bold'
                            }}>
                              {getStatusText(status)}
                            </div>
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#8B7355',
                            lineHeight: '1.5'
                          }}>
                            {dose.description}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{
          backgroundColor: '#FFF3E0',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
          fontSize: '13px',
          color: '#8B7355',
          lineHeight: '1.6'
        }}>
          ⚠️ <strong>참고사항</strong><br />
          • 이 일정은 질병관리청 표준 예방접종 일정입니다<br />
          • 실제 접종 일정은 소아청소년과 의사와 상담하세요<br />
          • 일부 백신은 제품에 따라 접종 횟수가 다를 수 있습니다<br />
          • 접종 기록은 예방접종도우미 앱에서 관리하세요
        </div>

        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '16px',
            backgroundColor: '#E8A87C',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#D4956B'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#E8A87C'}
        >
          닫기
        </button>
      </div>
    </div>
  );
}

export default VaccinationSchedule;
